<template>
  <div>
    <Breadcrumbs title="Currencies" />

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <h4 class="mb-0">
                  <i class="fas fa-coins me-2"></i>
                  Currency Management
                </h4>
                <div class="d-flex gap-2">
                  <button class="btn btn-primary" @click="openCreateModal" :disabled="!canCreate('packages')">
                    <i class="fas fa-plus me-1"></i>
                    Add Currency
                  </button>
                </div>
              </div>
            </div>

            <div class="card-body">
              <!-- Filters and Search -->
              <div class="row mb-4">
                <div class="col-md-4">
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="fas fa-search"></i>
                    </span>
                    <input
                      v-model="searchQuery"
                      type="text"
                      class="form-control"
                      placeholder="Search currencies..."
                      @input="handleSearch"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <select
                    v-model="filterStatus"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <select
                    v-model="sortBy"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="sortOrder">Sort Order</option>
                    <option value="code">Currency Code</option>
                    <option value="name">Name</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <button
                    class="btn btn-outline-secondary w-100"
                    @click="resetFilters"
                    title="Reset Filters"
                  >
                    <i class="fas fa-undo"></i>
                  </button>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Currencies Table -->
              <div v-else-if="currencies.length > 0" class="table-responsive table_style">
                <table class="table table-striped table-hover align-middle">
                  <thead class="table-dark">
                    <tr>
                      <th scope="col" style="width: 80px">Sr No</th>
                      <th scope="col" style="width: 100px">Code</th>
                      <th scope="col" style="min-width: 150px">Name</th>
                      <th scope="col" style="width: 100px">Symbol</th>
                      <th scope="col" style="width: 120px">Position</th>
                      <th scope="col" style="width: 100px">Decimals</th>
                      <th scope="col" style="width: 150px">Sort Order</th>
                      <th scope="col" style="width: 80px">Status</th>
                      <th scope="col" style="width: 120px">Sample</th>
                      <th scope="col" style="width: 150px">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="table-group-divider">
                    <tr
                      v-for="(currency, index) in currencies"
                      :key="currency._id"
                    >
                      <td class="text-center">
                        <span class="badge bg-secondary">
                          {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                        </span>
                      </td>
                      <td>
                        <strong class="text-primary">{{
                          currency.code
                        }}</strong>
                      </td>
                      <td>
                        <div>
                          <strong>{{ currency.name }}</strong>
                        </div>
                      </td>
                      <td class="text-center">
                        <span
                          class="currency-symbol"
                          :title="currency.symbol"
                          style="font-size: 1.2rem; font-weight: bold"
                        >
                          {{ currency.symbol }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span
                          :class="
                            currency.symbolPosition === 'before'
                              ? 'badge bg-info'
                              : 'badge bg-warning'
                          "
                        >
                          {{ currency.symbolPosition || "before" }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-light text-dark">
                          {{ currency.decimalPlaces || 2 }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-info">{{
                          currency.sortOrder || 0
                        }}</span>
                      </td>
                      <td class="text-center">
                        <div class="form-check form-switch">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="currency.isActive"
                            @change="handleToggleStatus(currency._id, $event)"
                            :disabled="loading || !canUpdate('packages')"
                          />
                          <label class="form-check-label text-xs">
                            {{ currency.isActive ? "Active" : "Inactive" }}
                          </label>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="currency-sample">
                          <code class="bg-light px-2 py-1 rounded">
                            {{ formatCurrencySample(currency) }}
                          </code>
                        </div>
                      </td>
                      <td>
                        <div class="d-flex gap-2" role="group">
                          <button
                            class="btn btn-sm btn-outline-primary"
                            @click="viewCurrency(currency)"
                            title="View"
                            :disabled="!canView('packages')"
                          >
                            <i class="fas fa-eye"></i>
                          </button>
                          <button
                            class="btn btn-sm btn-outline-success"
                            @click="editCurrency(currency)"
                            title="Edit"
                            :disabled="!canUpdate('packages')"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <button
                            class="btn btn-sm btn-outline-danger"
                            @click="deleteCurrency(currency._id)"
                            title="Delete"
                            :disabled="currency.code === 'USD' || !canDelete('packages')"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5">
                <i class="fas fa-coins fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No currencies found</h5>
                <p class="text-muted">
                  Add your first currency to get started.
                </p>
                <button class="btn btn-primary" @click="openCreateModal" :disabled="!canCreate('packages')">
                  <i class="fas fa-plus me-1"></i>
                  Add Currency
                </button>
              </div>

              <!-- Pagination -->
              <div
                v-if="totalItems > 0"
                class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top"
              >
                <div class="text-muted">
                  <small>
                    Showing {{ startItem }} to {{ endItem }} of
                    {{ totalItems }} currencies
                  </small>
                </div>
                <nav v-if="totalPages > 1" aria-label="Currencies pagination">
                  <ul class="pagination pagination-sm mb-0">
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage <= 1 }"
                    >
                      <button
                        class="page-link"
                        @click="changePage(currentPage - 1)"
                        :disabled="currentPage <= 1"
                        aria-label="Previous page"
                      >
                        <i class="fas fa-chevron-left"></i>
                        <span class="ms-1 d-none d-sm-inline">Previous</span>
                      </button>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === currentPage }"
                    >
                      <button
                        v-if="typeof page === 'number'"
                        class="page-link"
                        @click="changePage(page)"
                        :aria-label="`Go to page ${page}`"
                        :aria-current="
                          page === currentPage ? 'page' : undefined
                        "
                      >
                        {{ page }}
                      </button>
                      <span v-else class="page-link">{{ page }}</span>
                    </li>
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage >= totalPages }"
                    >
                      <button
                        class="page-link"
                        @click="changePage(currentPage + 1)"
                        :disabled="currentPage >= totalPages"
                        aria-label="Next page"
                      >
                        <span class="me-1 d-none d-sm-inline">Next</span>
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <CurrencyModal
      v-if="showModal"
      :show="showModal"
      :currency="selectedCurrency"
      :is-edit="isEditMode"
      @close="closeModal"
      @saved="handleCurrencySaved"
    />

    <!-- View Modal -->
    <CurrencyViewModal
      v-if="showViewModal"
      :show="showViewModal"
      :currency="selectedCurrency"
      @close="closeViewModal"
      @edit="handleEditFromView"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineAsyncComponent } from "vue";
import { toast } from "vue3-toastify";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

const CurrencyModal = defineAsyncComponent(
  () => import("@/components/theme/admin/packages/CurrencyModal.vue")
);

const CurrencyViewModal = defineAsyncComponent(
  () => import("@/components/theme/admin/packages/CurrencyViewModal.vue")
);

// State
const currencies = ref<any[]>([]);
const loading = ref(false);
const searchQuery = ref("");
const filterStatus = ref("");
const sortBy = ref("sortOrder");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalItems = ref(0);

// Modal state
const showModal = ref(false);
const showViewModal = ref(false);
const selectedCurrency = ref<any>(null);
const isEditMode = ref(false);

// Computed
const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage.value)
);

const startItem = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1
);

const endItem = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
);

const visiblePages = computed(() => {
  const delta = 2;
  const range = [];
  const rangeWithDots = [];

  if (totalPages.value <= 1) {
    return [1];
  }

  if (totalPages.value <= 7) {
    // Show all pages if total is 7 or less
    for (let i = 1; i <= totalPages.value; i++) {
      range.push(i);
    }
    return range;
  }

  for (
    let i = Math.max(2, currentPage.value - delta);
    i <= Math.min(totalPages.value - 1, currentPage.value + delta);
    i++
  ) {
    range.push(i);
  }

  if (currentPage.value - delta > 2) {
    rangeWithDots.push(1, "...");
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (currentPage.value + delta < totalPages.value - 1) {
    rangeWithDots.push("...", totalPages.value);
  } else if (totalPages.value > 1) {
    rangeWithDots.push(totalPages.value);
  }

  return rangeWithDots.filter(
    (page, index, array) => array.indexOf(page) === index
  );
});

// Methods
const fetchCurrencies = async () => {
  try {
    loading.value = true;
    const params: any = {
      page: currentPage.value,
      limit: itemsPerPage.value,
    };

    if (searchQuery.value) {
      params.search = searchQuery.value;
    }

    if (filterStatus.value !== "") {
      params.isActive = filterStatus.value;
    }

    if (sortBy.value) {
      params.sortBy = sortBy.value;
    }

    const response = await apiService.getCurrencies(params);

    // Handle the nested response structure similar to categories
    if (response.data && response.data.currencies) {
      currencies.value = response.data.currencies;
      totalItems.value =
        response.data.pagination?.totalItems || response.data.currencies.length;
    } else if (response.currencies) {
      // Fallback if response structure is different
      currencies.value = response.currencies;
      totalItems.value =
        response.pagination?.totalItems || response.currencies.length;
    } else {
      // Fallback for direct array response
      currencies.value = response.data || response || [];
      totalItems.value =
        response.total || response.pagination?.total || currencies.value.length;
    }
  } catch (error) {
    console.error("Error fetching currencies:", error);
    toast.error("Failed to load currencies");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  fetchCurrencies();
};

const handleFilter = () => {
  currentPage.value = 1;
  fetchCurrencies();
};

const resetFilters = () => {
  searchQuery.value = "";
  filterStatus.value = "";
  sortBy.value = "sortOrder";
  currentPage.value = 1;
  fetchCurrencies();
};

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchCurrencies();
  }
};

const openCreateModal = () => {
  selectedCurrency.value = null;
  isEditMode.value = false;
  showModal.value = true;
};

const editCurrency = (currency: any) => {
  selectedCurrency.value = { ...currency };
  isEditMode.value = true;
  showModal.value = true;
};

const viewCurrency = (currency: any) => {
  selectedCurrency.value = currency;
  showViewModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedCurrency.value = null;
  isEditMode.value = false;
};

const closeViewModal = () => {
  showViewModal.value = false;
  selectedCurrency.value = null;
};

const handleEditFromView = (currency: any) => {
  closeViewModal();
  editCurrency(currency);
};

const handleCurrencySaved = () => {
  fetchCurrencies();
  closeModal();
  toast.success(
    isEditMode.value
      ? "Currency updated successfully"
      : "Currency created successfully"
  );
};

const handleToggleStatus = (id: string, event: Event) => {
  const target = event.target as HTMLInputElement;
  toggleStatus(id, target.checked);
};

const toggleStatus = async (id: string, status: boolean) => {
  try {
    // Optimistic update - update UI immediately
    const currencyIndex = currencies.value.findIndex((curr) => curr._id === id);
    if (currencyIndex !== -1) {
      currencies.value[currencyIndex].isActive = status;
    }

    await apiService.toggleCurrencyStatus(id);
    toast.success(
      `Currency ${status ? "activated" : "deactivated"} successfully`
    );

    // Refresh data to ensure consistency
    await fetchCurrencies();
  } catch (error) {
    console.error("Error toggling status:", error);
    toast.error("Failed to update currency status");

    // Revert optimistic update on error
    const currencyIndex = currencies.value.findIndex((curr) => curr._id === id);
    if (currencyIndex !== -1) {
      currencies.value[currencyIndex].isActive = !status;
    }
  }
};

const deleteCurrency = async (id: string) => {
  if (
    !confirm(
      "Are you sure you want to delete this currency? This action cannot be undone."
    )
  ) {
    return;
  }

  try {
    await apiService.deleteCurrency(id);
    await fetchCurrencies();
    toast.success("Currency deleted successfully");
  } catch (error) {
    console.error("Error deleting currency:", error);
    toast.error("Failed to delete currency");
  }
};

// Utility functions
const formatNumber = (value: number, decimals: number = 2): string => {
  return Number(value).toFixed(decimals);
};

const formatCurrencySample = (currency: any): string => {
  const amount = "123.45";
  const formattedAmount = formatNumber(
    parseFloat(amount),
    currency.decimalPlaces || 2
  );

  if (currency.symbolPosition === "after") {
    return `${formattedAmount}${currency.symbol}`;
  }
  return `${currency.symbol}${formattedAmount}`;
};

// Lifecycle
onMounted(() => {
  fetchCurrencies();
});
</script>

<style scoped>
.currency-symbol {
  display: inline-block;
  min-width: 20px;
  text-align: center;
}

.currency-sample {
  font-family: "Courier New", monospace;
}

/* Enhanced table header styling */
.table thead.table-dark th {
  background-color: var(--bs-dark) !important;
  color: var(--bs-white) !important;
  border-color: var(--bs-dark) !important;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.025em;
}

.table th,
.table td {
  vertical-align: middle;
  padding: 0.75rem;
}

.table {
  margin-bottom: 0;
}

/* Enhanced pagination styling */
.pagination .page-link {
  color: #495057;
  background-color: #fff;
  border: 1px solid #dee2e6;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  border-radius: 0;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out;
}

.pagination .page-link:hover {
  z-index: 2;
  color: #0056b3;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.pagination .page-item:first-child .page-link {
  margin-left: 0;
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.pagination .page-item:last-child .page-link {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

.pagination .page-item.active .page-link {
  z-index: 3;
  color: #fff;
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
  cursor: not-allowed;
}

/* Table responsiveness improvements */
.table-responsive {
  border-radius: 0.375rem;
  overflow: hidden;
}

.btn-group .btn {
  border-radius: 0.25rem;
  margin-right: 2px;
}

.btn-group .btn:last-child {
  margin-right: 0;
}

code {
  font-size: 0.8rem;
}

.badge {
  font-size: 0.75rem;
}

.text-xs {
  font-size: 0.75rem;
}

/* Card styling improvements */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}
.table_style table{
  min-width: 1300px;
}
.table_style{
  overflow-x:auto;
}
</style>
